import {
  Alert,
  AlertTitle,
  Avatar, Box, Button, Divider, Grid, Stack, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { grey } from "@mui/material/colors";
import { useResponse } from "../utils/itemDataProvider";
import { useFormData } from "../utils/formDataProvider";
import MUIModal from "../common/MuiModal";
import { useReturnItemData } from "../utils/returnDataProvider";
import { apiConstants, postGeneric } from "../common/constants";
import { useSnackbar } from "notistack";
import ReturnData from "./ReturnDetails";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "../utils/Router";
import AppConfig from "../utils/config";
import { formatDateToDDMMYYYY } from "../utils/helper";
import FullOrderReturn from "./FullOrderReturn";
import ImageCarousel from "./ImageCarousel";
import ConfirmDialog from "../common/ConfirmDialog";
import ActionButton from "./ActionButton";
import { useDomainConfig } from "../utils/domainConfigProvider";

function ItemList() {
  const theme = useTheme();
  const { domainConfig } = useDomainConfig();
  const isNuvah = domainConfig?.brandUrl === AppConfig?.CustomeUI?.NuvahURL;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalData, setModalData] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [selectedItem, setSelectedItem] = useState(null);
  const { navigate } = useNavigate();
  const { response, clearResponse } = useResponse();
  const { awbNumber } = useFormData();
  const { returnData, clearReturnData, setReturnData } = useReturnItemData();

  const handleClose = () => {
    setOpen(false);
    setFullOpen(false);
  };

  // return to home on reload
  useEffect(() => {
    const navigationEntries = performance.getEntriesByType("navigation");
    if (navigationEntries.length > 0) {
      const lastNavigation = navigationEntries[navigationEntries.length - 1];
      switch (lastNavigation.type) {
        case "reload":
          navigate('/');
          break;
        case "navigate":
          break;
        default:
          console.log('Page loaded for the first time.');
          break;
      }
    }
  }, [navigate]);

  // place return request 
  const handleClick = () => {
    setIsConfirm(true)
  }

  const result = isReturnDataValid(response?.itemData, returnData);

  const handleConfirmAction = () => {
    setIsSubmitting(true)
    let formData = new FormData();
    let returnAndExchangeJson = {
      id: response?.id,
      userPhoneNumber: response?.userPhoneNumber,
      awbNumber: awbNumber,
      // rvpReason: "Return",
      itemData: [],
      otp: {
        otpValue: response?.otp?.otpValue
      }
    };
    returnData.forEach(entry => {
      let item = {
        item: {
          units: entry.data.item?.units,
          sku_id: entry.data.item?.sku_id,
        },
        remainingItems: modalData?.remainingItems,
        alreadyReturnedUnits: modalData?.alreadyReturnedUnits,
        totalFwdUnits: modalData?.totalFwdUnits,
        rvpDesc: entry.data?.rvpDesc,
        skuRvpReason: entry.data?.skuRvpReason,
      };
      if(result){
        returnAndExchangeJson.rvpReason = entry.data?.skuRvpReason;
      }
      returnAndExchangeJson.itemData.push(item);
      entry.list.forEach((key, index) => {
        if (key.file && key.file[0]) {
          formData.append(`${entry.data.item.sku_id}.${index}`, key.file[0]);
        }
      });
    });
    if (result) {
      returnAndExchangeJson.isAllOrderReturn = true;
      returnAndExchangeJson.isPartialReturn = false;
    } else {
      returnAndExchangeJson.isAllOrderReturn = false;
      returnAndExchangeJson.isPartialReturn = true;
    }
    formData.append('returnAndExchangeJson', JSON.stringify(returnAndExchangeJson));
    postGeneric(`${apiConstants.RETURN_DATA}`, formData).then((res) => {
      setIsSubmitting(false)
      if (res.status === 200) {
        postGeneric(`${apiConstants.SEND_RETURN_PLACED_MESSAGE}`, {
          requestedItems: returnData?.map(entry => ({
            [entry.data.item?.sku_id]: entry.data.item?.units
          })),
          id: response?.id,
          fromMerchant: modalData.fromMerchant
        }).then((res) => {
        })
        enqueueSnackbar("Return Request Placed Successfully!", { variant: 'success' })
        navigate('/')
        clearReturnData()
        clearResponse()
      }
    }).catch((err) => {
      setIsSubmitting(false)
      enqueueSnackbar(err?.response?.data?.message || "Some error occurred!", { variant: 'error' })
      console.log(err);
    });
  }

  const handleImageClick = (item) => {
    if (item === 'NA' || (Array.isArray(item) && item.length === 0)) {
      return null;
    } else {
      const itemUrl = item?.split(',')
      setSelectedItem(itemUrl);
      setImageOpen(true);
      // console.log(itemUrl);
    }
  };
  const isDataFilled = (sku_id) => {
    return returnData?.some(entry => entry.data.item.sku_id === sku_id);
  }

  const moddata = (sku_id) => {
    const entry = returnData?.find((val) => val.data?.item?.sku_id === sku_id);
    if (entry) {
      return entry.data.item.units;
    }
    return null;
  };

  const handleFullreturn = () => {
    setFullOpen(true)
  }


  return (
    <>
      <Grid container spacing={2}>
        {/* Top Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: grey[100],
              borderRadius: 2,
              p: 2,
              border: `1px solid ${grey[300]}`,
            }}
          >
            {/* AWB Number and Return Date Info */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              mb={2}
            >
              <Typography variant="subtitle2">
                <strong>AWB Number:</strong> {awbNumber}
              </Typography>
              <Typography
                variant="subtitle2"
                color={response?.isLastDateExpired ? 'error' : 'textPrimary'}
              >
                <strong>
                  {response?.isLastDateExpired ? 'Return window closed on' : 'Return By'}:
                </strong>{' '}
                {formatDateToDDMMYYYY(response?.lastReturnDate)}
              </Typography>
            </Stack>

            {/* Instructions */}
            <Alert
              severity="info"
              variant={isNuvah ? "filled" : "standard"}
              sx={{
                mb: 2,
                ...(isNuvah && {
                  backgroundColor: AppConfig.CustomeUI.NuvahBrandColor,
                  color: '#fff',
                  '& .MuiAlertTitle-root': { color: '#fff' },
                  '& .MuiAlert-icon': { color: '#fff' },
                }),
              }}
            >
              <AlertTitle>Instructions</AlertTitle>
              <Typography variant="body2">
                <strong>Return All Items:</strong> Click this button to return the entire order.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Return Selected Items:</strong> Check the box next to each item and click the button.
              </Typography>
            </Alert>

            {/* Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems="center"
            >
              <Button
                onClick={handleFullreturn}
                variant="contained"
                disabled={
                  response?.isAllOrderReturn ||
                  response?.partialReturn ||
                  response?.isAllItemNonReturnable
                }
                fullWidth={isSmallScreen}
              >
                Return All Items
              </Button>
              <LoadingButton
                loading={isSubmitting}
                onClick={handleClick}
                disabled={!(returnData.length > 0)}
                variant="contained"
                aria-label="Submit return request for selected items"
                fullWidth={isSmallScreen}
              >
                Return Selected Items
              </LoadingButton>
            </Stack>
          </Box>
        </Grid>

        {/* Optional Message if Return Request Placed */}
        {response?.isAllOrderReturn && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="error">
              Order return request placed
            </Typography>
          </Grid>
        )}

        {/* Scrollable Item List */}
        <Grid item xs={12}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: grey[300],
              borderRadius: 2,
              minHeight: 100,
              maxHeight: 400,
              overflowY: 'auto',
              p: 1,
            }}
          >
            {response?.itemData?.map((val, index) => {
              const dataFilled = isDataFilled(val?.item?.sku_id);
              return (
                <Box key={index} sx={{ mb: 2 }}>
                  <Stack
                    direction={isSmallScreen ? 'column' : 'row'}
                    spacing={2}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: grey[300],
                      borderRadius: 1,
                    }}
                  >
                    {/* Image and optional ActionButton (mobile) */}
                    <Box
                      display="flex"
                      flexDirection={isSmallScreen ? 'row' : 'column'}
                      alignItems="center"
                      justifyContent="space-between"
                      flex={0.6}
                    >
                      <Avatar
                        variant="rounded"
                        src={val?.item?.item_url?.split(',')[0] || AppConfig.ItemSKU.image}
                        onClick={val.item?.item_url ? () => handleImageClick(val.item?.item_url) : undefined}
                        sx={{
                          width: isSmallScreen ? 100 : '100%',
                          height: 100,
                          transition: 'transform 0.3s, box-shadow 0.3s',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.2)',
                          },
                        }}
                      />
                      {isSmallScreen && (
                        <ActionButton
                          val={val}
                          setOpen={setOpen}
                          setModalData={setModalData}
                          dataFilled={dataFilled}
                          clearReturnData={clearReturnData}
                          returnData={returnData}
                          setReturnData={setReturnData}
                        />
                      )}
                    </Box>

                    {/* Item Info */}
                    <Box flex={3}>
                      <Typography variant="subtitle1">{val?.item?.item_name}</Typography>

                      <Stack
                        direction="row"
                        spacing={2}
                        divider={<Divider orientation="vertical" flexItem />}
                        sx={{ my: 1 }}
                      >
                        <Typography variant="caption" fontWeight={500}>
                          SKU ID: {val?.item?.sku_id}
                        </Typography>
                        <Typography variant="caption" fontWeight={500}>
                          Total Units: {val?.item?.units}
                        </Typography>
                        <Typography variant="caption" fontWeight={500}>
                          Price: {val?.item?.selling_price}
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={2}
                        divider={<Divider orientation="vertical" flexItem />}
                        sx={{ my: 1 }}
                      >
                        <Typography variant="caption" fontWeight={500}>
                          Approved Units: {val?.alreadyReturnedUnits || 0}
                        </Typography>
                        <Typography variant="caption" fontWeight={500}>
                          Requested Units: {val?.requestedReturnItems || 0}
                        </Typography>
                        <Typography variant="caption" fontWeight={500}>
                          Rejected Units: {val?.rejectedItems || 0}
                        </Typography>
                      </Stack>

                      {dataFilled && (
                        <Typography variant="caption" color={isNuvah ? "primary" : "error"} fontWeight={500}>
                          Requesting Return Units: {moddata(val?.item?.sku_id)}
                        </Typography>
                      )}
                      {val?.totalFwdUnits - val?.requestedReturnItems === 0 && (
                        <Typography variant="caption" color="error" fontWeight={500}>
                          All units return request placed
                        </Typography>
                      )}
                      {!val?.isReturnAllowed && (
                        <Typography variant="caption" color="error" fontWeight={500}>
                          Return for this item is not allowed
                        </Typography>
                      )}
                    </Box>

                    {/* Desktop Action Button */}
                    {!isSmallScreen && (
                      <Box alignSelf="center">
                        <ActionButton
                          val={val}
                          setOpen={setOpen}
                          setModalData={setModalData}
                          dataFilled={dataFilled}
                          clearReturnData={clearReturnData}
                          returnData={returnData}
                          setReturnData={setReturnData}
                        />
                      </Box>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Grid>
      </Grid>

      <ImageCarousel open={imageOpen} onClose={() => setImageOpen(false)} images={selectedItem} />
      <MUIModal open={open} handleClose={handleClose} title={"Please enter the details below"} maxWidth={'md'}
        children={<ReturnData modalData={modalData} setOpen={setOpen} />} />

      <MUIModal open={fullOpen} handleClose={handleClose} title={"Please enter the details below"} maxWidth={'md'}
        children={<FullOrderReturn setFullOpen={setFullOpen} />} />

      <ConfirmDialog
        title=''
        open={isConfirm}
        setOpen={setIsConfirm}
        onConfirm={() => handleConfirmAction()}>
        Are you sure you want to return selected items ?
      </ConfirmDialog>
    </>


  );
}
export default ItemList;


const isReturnDataValid = (itemData = [], returnData = []) => {
  // Step 1: Total units from itemData
  const totalItemUnits = itemData.reduce((sum, item) => {
    return sum + (item?.item?.units || 0);
  }, 0);

  // Step 2: Total units from returnData
  const totalReturnUnits = returnData.reduce((sum, entry) => {
    return sum + (entry?.data?.item?.units || 0);
  }, 0);

  // Step 3: Compare total units
  if (totalItemUnits !== totalReturnUnits) {
    return false;
  }

  // Step 4: Compare rvpReasons per sku_id
  const skuToReasonMap = new Map();
  for (let entry of returnData) {
    const sku = entry?.data?.item?.sku_id;
    const reason = entry?.rvpReason;
    if (!sku || !reason) return false;

    // Track reasons per SKU
    if (!skuToReasonMap.has(sku)) {
      skuToReasonMap.set(sku, reason);
    } else {
      // In case same SKU appears twice, ensure reasons match
      if (skuToReasonMap.get(sku) !== reason) {
        return false;
      }
    }
  }

  // Ensure all reasons are the same
  const reasons = Array.from(skuToReasonMap.values());
  const allSameReason = reasons.every((r) => r === reasons[0]);

  return allSameReason;
};
